import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';

export const useAddRecipeFavorite = () => {
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const addRecipeFavorite = async (recipeId) => {
        setIsAdding(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch('/api/v1/recipe-favorite', {
                method: 'POST',
                headers,
                body: JSON.stringify({ recipe_id: recipeId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add recipe to favorites');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsAdding(false);
        }
    };

    return {
        addRecipeFavorite,
        isAdding,
        error
    };
};

export const useDeleteRecipeFavorite = () => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const deleteRecipeFavorite = async (recipeId) => {
        setIsDeleting(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/recipe-favorite/recipe/${recipeId}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to remove recipe from favorites');
            }

            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        deleteRecipeFavorite,
        isDeleting,
        error
    };
};

export const useGetRecipeFavoriteStatus = () => {
    const [isChecking, setIsChecking] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const getRecipeFavoriteStatus = async (recipeId) => {
        setIsChecking(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const headers = {
                'Authorization': `Bearer ${sessionToken}`
            };

            const response = await fetch(`/api/v1/recipe-favorite/recipe/${recipeId}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to check recipe favorite status');
            }

            const data = await response.json();
            return data.isFavorite;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsChecking(false);
        }
    };

    return {
        getRecipeFavoriteStatus,
        isChecking,
        error
    };
};

export const useGetAllFavoriteRecipes = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const { sessionToken } = useAuth();

    const getAllFavoriteRecipes = React.useCallback(async (sessionToken) => {
        if (!sessionToken) {
            setError('No session token provided');
            setFavoriteRecipes([]);
            return [];
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/recipe-favorite/my-favorites', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            console.log('getAllFavoriteRecipes (hook) status:', response.status);
            console.log('getAllFavoriteRecipes (hook) statusText:', response.statusText);
            
            const data = await response.json();
            console.log('getAllFavoriteRecipes (hook) data:', data);

            if (!response.ok) {
                throw new Error(data.error || 'Failed to fetch favorite recipes');
            }
            
            // Extract just the recipe_ids into an array of numbers
            const recipeIds = data.map(favorite => Number(favorite.recipe_id));
            console.log('Extracted recipe IDs:', recipeIds);
            setFavoriteRecipes(recipeIds);
            return recipeIds;

        } catch (err) {
            setError(err.message);
            setFavoriteRecipes([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    return {
        getAllFavoriteRecipes,
        favoriteRecipes,
        isLoading,
        error
    };
};

